import React, { useCallback, useEffect, useState } from 'react'

let listeners = []
let idCounter = 0

const emit = (toast) => {
  listeners.forEach((listener) => listener(toast))
}

const addToast = (type, message, options = {}) => {
  const { details, timeout = 5000 } =
    typeof options === 'number' ? { timeout: options } : options
  emit({ id: ++idCounter, type, message, details, timeout })
}

// Imperative API so any code (components, axios interceptors, catch
// blocks outside of React) can raise a toast without needing context.
// Second arg is either a timeout in ms, or { details, timeout } where
// `details` is a raw error string shown behind a "Details" accordion.
export const toast = {
  success: (message, options) => addToast('success', message, options),
  error: (message, options) => addToast('error', message, options),
  info: (message, options) => addToast('info', message, options),
}

const typeStyles = {
  success: 'bg-emerald-500 border-emerald-600',
  error: 'bg-red-500 border-red-600',
  info: 'bg-slate-700 border-slate-800',
}

// Mount once near the root of the app; toasts render above everything else.
export const ToastContainer = () => {
  const [toasts, setToasts] = useState([])

  const removeToast = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  useEffect(() => {
    const listener = (newToast) => {
      setToasts((current) => [...current, newToast])
      setTimeout(() => removeToast(newToast.id), newToast.timeout)
    }
    listeners.push(listener)
    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  }, [removeToast])

  if (toasts.length === 0) return null

  return (
    <div className="fixed top-5 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 w-full max-w-md px-4 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto text-white text-sm font-semibold rounded-xl border-2 shadow-lg p-4 ${typeStyles[t.type]}`}
        >
          <div className="flex items-start justify-between gap-3">
            <span>{t.message}</span>
            <button
              type="button"
              onClick={() => removeToast(t.id)}
              aria-label="Dismiss"
              className="shrink-0 leading-none text-white/80 hover:text-white"
            >
              &times;
            </button>
          </div>
          {t.details && (
            <details className="mt-2 font-normal">
              <summary className="cursor-pointer text-white/80 text-xs select-none">
                Details
              </summary>
              <pre className="mt-1 whitespace-pre-wrap break-words text-xs bg-black/20 rounded-lg p-2">
                {t.details}
              </pre>
            </details>
          )}
        </div>
      ))}
    </div>
  )
}
